import { supabase } from '../lib/supabase';
import { bankTransferService } from './bankTransferService';
import { paymentConfirmationService } from './paymentConfirmationService';

export const salaryDistributionOrchestrationService = {
  /**
   * Create salary distribution orchestration from payroll run
   * Automatically distributes salaries across multiple PNG banks
   */
  async createOrchestration(payrollRunId, createdBy) {
    try {
      // 1. Validate payroll run
      const { data: payrollRun, error: runError } = await supabase
        ?.from('payroll_runs')
        ?.select('*')
        ?.eq('id', payrollRunId)
        ?.eq('payroll_status', 'processed')
        ?.single();

      if (runError) throw runError;
      if (!payrollRun) throw new Error('Payroll run not found or not processed');

      // 2. Get all payslips with bank accounts
      const { data: payslips, error: payslipsError } = await supabase
        ?.from('payslips')
        ?.select(`
          *,
          employees (
            id,
            employee_number,
            first_name,
            last_name,
            email
          ),
          payroll_details (
            net_pay
          )
        `)
        ?.eq('payroll_run_id', payrollRunId)
        ?.eq('payslip_status', 'generated');

      if (payslipsError) throw payslipsError;
      if (!payslips || payslips?.length === 0) {
        throw new Error('No generated payslips found');
      }

      // 3. Get bank accounts for all employees
      const employeeIds = payslips?.map(p => p?.employee_id);
      const { data: bankAccounts, error: accountsError } = await supabase
        ?.from('employee_bank_accounts')
        ?.select('*')
        ?.in('employee_id', employeeIds)
        ?.eq('is_active', true)
        ?.eq('is_primary', true);

      if (accountsError) throw accountsError;

      // 4. Group by bank
      const bankGroups = {};
      const accountLookup = {};
      
      bankAccounts?.forEach(account => {
        accountLookup[account?.employee_id] = account;
        if (!bankGroups?.[account?.bank_name]) {
          bankGroups[account?.bank_name] = [];
        }
      });

      // 5. Validate and group payslips by bank
      let totalEmployees = 0;
      let totalAmount = 0;
      const invalidEmployees = [];

      payslips?.forEach(payslip => {
        const account = accountLookup?.[payslip?.employee_id];
        const netPay = payslip?.payroll_details?.net_pay || 0;

        if (!account || netPay <= 0) {
          invalidEmployees?.push({
            employeeId: payslip?.employee_id,
            employeeName: `${payslip?.employees?.first_name} ${payslip?.employees?.last_name}`,
            reason: !account ? 'No bank account' : 'Invalid net pay'
          });
          return;
        }

        bankGroups?.[account?.bank_name]?.push({
          payslip,
          account,
          netPay
        });

        totalEmployees++;
        totalAmount += netPay;
      });

      if (totalEmployees === 0) {
        throw new Error('No valid employees with bank accounts found');
      }

      // 6. Generate orchestration number
      const datePrefix = new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, '');
      const orchestrationNumber = `DIST-${datePrefix}-${String(Math?.floor(Math?.random() * 10000))?.padStart(4, '0')}`;

      // 7. Create orchestration record
      const { data: orchestration, error: orchError } = await supabase
        ?.from('salary_distribution_orchestrations')
        ?.insert({
          orchestration_number: orchestrationNumber,
          payroll_run_id: payrollRunId,
          distribution_status: 'pending',
          total_employees: totalEmployees,
          total_amount_kina: totalAmount,
          total_banks: Object?.keys(bankGroups)?.length,
          reconciliation_method: 'automatic',
          created_by: createdBy,
          metadata: {
            invalid_employees: invalidEmployees,
            bank_breakdown: Object?.keys(bankGroups)?.map(bank => ({
              bank_name: bank,
              employee_count: bankGroups?.[bank]?.length,
              total_amount: bankGroups?.[bank]?.reduce((sum, item) => sum + item?.netPay, 0)
            }))
          }
        })
        ?.select()
        ?.single();

      if (orchError) throw orchError;

      // 8. Create bank distribution batches
      const batchPromises = Object?.keys(bankGroups)?.map(async (bankName) => {
        const bankData = bankGroups?.[bankName];
        const bankTotal = bankData?.reduce((sum, item) => sum + item?.netPay, 0);

        // Create EFT transfer batch
        const batchResult = await bankTransferService?.createTransferBatchFromPayrollRun(
          payrollRunId,
          bankName,
          createdBy
        );

        // Create bank distribution batch
        const { data: distBatch, error: batchError } = await supabase
          ?.from('bank_distribution_batches')
          ?.insert({
            orchestration_id: orchestration?.id,
            eft_transfer_batch_id: batchResult?.batchId,
            bank_name: bankName,
            transfer_method: 'eft_file',
            batch_status: 'draft',
            employee_count: bankData?.length,
            total_amount_kina: bankTotal,
            reconciliation_status: 'pending'
          })
          ?.select()
          ?.single();

        if (batchError) throw batchError;

        // Create payment confirmations for each employee
        const confirmations = bankData?.map(item => ({
          orchestration_id: orchestration?.id,
          bank_distribution_batch_id: distBatch?.id,
          eft_transfer_record_id: null, // Will be linked later
          employee_id: item?.payslip?.employee_id,
          confirmation_number: `CONF-${datePrefix}-${item?.payslip?.employee_number}-${Math?.floor(Math?.random() * 1000)}`,
          payment_amount_kina: item?.netPay,
          bank_name: bankName,
          account_number: item?.account?.account_number,
          confirmation_status: 'pending',
          reconciliation_status: 'pending',
          reconciliation_method: 'automatic'
        }));

        const { error: confError } = await supabase
          ?.from('payment_confirmations')
          ?.insert(confirmations);

        if (confError) throw confError;

        return distBatch;
      });

      await Promise?.all(batchPromises);

      return {
        success: true,
        orchestrationId: orchestration?.id,
        orchestrationNumber: orchestration?.orchestration_number,
        totalEmployees,
        totalAmount,
        totalBanks: Object?.keys(bankGroups)?.length,
        invalidEmployees
      };
    } catch (error) {
      console.error('Error creating salary distribution orchestration:', error);
      throw error;
    }
  },

  /**
   * Start distribution processing
   */
  async startDistribution(orchestrationId) {
    try {
      const { data, error } = await supabase
        ?.from('salary_distribution_orchestrations')
        ?.update({
          distribution_status: 'processing',
          started_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', orchestrationId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Update all bank batches to processing
      await supabase
        ?.from('bank_distribution_batches')
        ?.update({
          batch_status: 'validated',
          processing_started_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('orchestration_id', orchestrationId);

      return data;
    } catch (error) {
      console.error('Error starting distribution:', error);
      throw error;
    }
  },

  /**
   * Get orchestration details with real-time status
   */
  async getOrchestrationDetails(orchestrationId) {
    try {
      const { data: orchestration, error: orchError } = await supabase
        ?.from('salary_distribution_orchestrations')
        ?.select(`
          *,
          payroll_runs (
            pay_period_start,
            pay_period_end,
            payment_date
          )
        `)
        ?.eq('id', orchestrationId)
        ?.single();

      if (orchError) throw orchError;

      const { data: batches, error: batchError } = await supabase
        ?.from('bank_distribution_batches')
        ?.select('*')
        ?.eq('orchestration_id', orchestrationId);

      if (batchError) throw batchError;

      const { data: confirmations, error: confError } = await supabase
        ?.from('payment_confirmations')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            email
          )
        `)
        ?.eq('orchestration_id', orchestrationId);

      if (confError) throw confError;

      return {
        orchestration: {
          id: orchestration?.id,
          orchestrationNumber: orchestration?.orchestration_number,
          distributionStatus: orchestration?.distribution_status,
          totalEmployees: orchestration?.total_employees,
          totalAmountKina: orchestration?.total_amount_kina,
          processedEmployees: orchestration?.processed_employees,
          failedEmployees: orchestration?.failed_employees,
          totalBanks: orchestration?.total_banks,
          banksCompleted: orchestration?.banks_completed,
          reconciliationProgressPercentage: orchestration?.reconciliation_progress_percentage,
          startedAt: orchestration?.started_at,
          completedAt: orchestration?.completed_at,
          payrollRun: orchestration?.payroll_runs
        },
        batches: batches?.map(batch => ({
          id: batch?.id,
          bankName: batch?.bank_name,
          transferMethod: batch?.transfer_method,
          batchStatus: batch?.batch_status,
          employeeCount: batch?.employee_count,
          totalAmountKina: batch?.total_amount_kina,
          processedCount: batch?.processed_count,
          failedCount: batch?.failed_count,
          reconciliationStatus: batch?.reconciliation_status,
          reconciledAt: batch?.reconciled_at,
          bankConfirmationReference: batch?.bank_confirmation_reference
        })),
        confirmations: confirmations?.map(conf => ({
          id: conf?.id,
          confirmationNumber: conf?.confirmation_number,
          employeeId: conf?.employee_id,
          employeeName: `${conf?.employees?.first_name} ${conf?.employees?.last_name}`,
          employeeNumber: conf?.employees?.employee_number,
          paymentAmountKina: conf?.payment_amount_kina,
          bankName: conf?.bank_name,
          accountNumber: conf?.account_number,
          confirmationStatus: conf?.confirmation_status,
          reconciliationStatus: conf?.reconciliation_status,
          reconciledAt: conf?.reconciled_at,
          bankTransactionId: conf?.bank_transaction_id,
          confirmationSentToEmployee: conf?.confirmation_sent_to_employee
        }))
      };
    } catch (error) {
      console.error('Error fetching orchestration details:', error);
      throw error;
    }
  },

  /**
   * Record reconciliation event
   */
  async recordReconciliationEvent(eventData) {
    try {
      const { data, error } = await supabase
        ?.from('reconciliation_events')
        ?.insert({
          orchestration_id: eventData?.orchestrationId,
          payment_confirmation_id: eventData?.paymentConfirmationId,
          event_type: eventData?.eventType,
          event_status: eventData?.eventStatus,
          event_source: eventData?.eventSource,
          bank_name: eventData?.bankName,
          transaction_reference: eventData?.transactionReference,
          amount_kina: eventData?.amountKina,
          reconciliation_method: eventData?.reconciliationMethod || 'automatic',
          matched_automatically: eventData?.matchedAutomatically || false,
          confidence_score: eventData?.confidenceScore || 0,
          event_timestamp: eventData?.eventTimestamp || new Date()?.toISOString(),
          event_data: eventData?.eventData
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error recording reconciliation event:', error);
      throw error;
    }
  },

  /**
   * Send payment confirmations to employees
   */
  async sendEmployeeConfirmations(orchestrationId) {
    try {
      const { data: confirmations, error } = await supabase
        ?.from('payment_confirmations')
        ?.select(`
          *,
          employees (
            email,
            first_name,
            last_name
          )
        `)
        ?.eq('orchestration_id', orchestrationId)
        ?.eq('confirmation_sent_to_employee', false)
        ?.eq('reconciliation_status', 'reconciled');

      if (error) throw error;

      const sendPromises = confirmations?.map(async (conf) => {
        try {
          // Send confirmation email
          await paymentConfirmationService?.sendPaymentConfirmation({
            payment: {
              amount: conf?.payment_amount_kina,
              timestamp: conf?.bank_confirmation_timestamp || conf?.created_at,
              transactionId: conf?.bank_transaction_id,
              receiptNumber: conf?.confirmation_number,
              id: conf?.id
            },
            invoice: {
              invoiceNumber: conf?.confirmation_number,
              amountDue: conf?.payment_amount_kina
            },
            company: {
              contactEmail: conf?.employees?.email,
              companyName: `${conf?.employees?.first_name} ${conf?.employees?.last_name}`
            },
            paymentMethod: conf?.bank_name?.toLowerCase(),
            reconciliationStatus: 'reconciled'
          });

          // Mark as sent
          await supabase
            ?.from('payment_confirmations')
            ?.update({
              confirmation_sent_to_employee: true,
              confirmation_sent_at: new Date()?.toISOString(),
              updated_at: new Date()?.toISOString()
            })
            ?.eq('id', conf?.id);

          return { success: true, confirmationId: conf?.id };
        } catch (err) {
          console.error(`Failed to send confirmation ${conf?.id}:`, err);
          return { success: false, confirmationId: conf?.id, error: err?.message };
        }
      });

      const results = await Promise?.all(sendPromises);
      return results;
    } catch (error) {
      console.error('Error sending employee confirmations:', error);
      throw error;
    }
  },

  /**
   * Get reconciliation dashboard data
   */
  async getReconciliationDashboard(filters = {}) {
    try {
      let query = supabase
        ?.from('salary_distribution_orchestrations')
        ?.select(`
          *,
          bank_distribution_batches (
            id,
            bank_name,
            reconciliation_status,
            total_amount_kina
          ),
          payment_confirmations (
            id,
            reconciliation_status,
            payment_amount_kina
          )
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('distribution_status', filters?.status);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      const { data, error } = await query?.limit(50);

      if (error) throw error;

      return data?.map(orch => ({
        id: orch?.id,
        orchestrationNumber: orch?.orchestration_number,
        distributionStatus: orch?.distribution_status,
        totalEmployees: orch?.total_employees,
        totalAmountKina: orch?.total_amount_kina,
        totalBanks: orch?.total_banks,
        reconciliationProgressPercentage: orch?.reconciliation_progress_percentage,
        startedAt: orch?.started_at,
        completedAt: orch?.completed_at,
        bankBreakdown: orch?.bank_distribution_batches?.map(batch => ({
          bankName: batch?.bank_name,
          reconciliationStatus: batch?.reconciliation_status,
          totalAmount: batch?.total_amount_kina
        })),
        reconciliationSummary: {
          total: orch?.payment_confirmations?.length || 0,
          reconciled: orch?.payment_confirmations?.filter(c => c?.reconciliation_status === 'reconciled')?.length || 0,
          pending: orch?.payment_confirmations?.filter(c => c?.reconciliation_status === 'pending')?.length || 0
        }
      }));
    } catch (error) {
      console.error('Error fetching reconciliation dashboard:', error);
      throw error;
    }
  },

  /**
   * Format amount in PNG Kina
   */
  formatAmount(amount) {
    return new Intl.NumberFormat('en-PG', {
      style: 'currency',
      currency: 'PGK',
      currencyDisplay: 'code'
    })?.format(amount)?.replace('PGK', 'K');
  }
};

export default salaryDistributionOrchestrationService;